'use client';
import { cn } from '@/lib/cn';
import Link from 'next/link';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import qs from 'query-string';
import React from 'react';
import { writeToSessionStorage } from '@/lib/session-storage/session-storage';
import { useReadLocalStorage } from 'usehooks-ts';
import { buttonVariants } from './button';

interface DetailModalLinkProps {
  pathname: string;
  children: React.ReactNode;
  className?: string;
  ignoreDefaultClassName?: boolean;
  query?: { page: string; im: boolean };
  target?: React.HTMLAttributeAnchorTarget;
  ref?: React.Ref<HTMLAnchorElement>;
}

export const DETAIL_MODAL_SOURCE_KEY = 'detail_modal_source';
export const BULK_MODAL_SOURCE_KEY = 'bulk_modal_source';
export const CONTRACT_MODAL_SOURCE_KEY = 'contract_modal_source';
export const INVOICING_MODAL_SOURCE_KEY = 'invoicing_modal_source';

type IOverrides =
  | typeof CONTRACT_MODAL_SOURCE_KEY
  | typeof BULK_MODAL_SOURCE_KEY
  | typeof INVOICING_MODAL_SOURCE_KEY;

type IUrlStringOrhMethod = string | ((id?: string) => string);

export const useOpenDetailModal = (
  urlStringOrMethod: IUrlStringOrhMethod,
  overrideKey?: IOverrides,
  ignoreDetailPageBlock?: boolean
) => {
  const router = useRouter();
  const currentPathname = usePathname();
  const query = useSearchParams().toString();
  const scroll = useReadLocalStorage<number>('scrollPosition') || null;
  const isDetailPage = currentPathname.includes('/detail/');
  const setDetailModalSourcePage = () => {
    if (isDetailPage && !ignoreDetailPageBlock) return;
    const parsedQuery = qs.parse(query);
    const newQuery = qs.stringify(
      { ...parsedQuery, scroll: scroll },
      {
        skipEmptyString: true,
        skipNull: true,
      }
    );
    writeToSessionStorage({
      key: overrideKey ?? DETAIL_MODAL_SOURCE_KEY,
      value: newQuery ? `${currentPathname}?${newQuery}` : currentPathname,
    });
  };

  const navigate = (id?: string) => {
    let url: string;
    if (typeof urlStringOrMethod === 'function') {
      url = urlStringOrMethod(id);
    } else if (typeof urlStringOrMethod === 'string') {
      url = urlStringOrMethod;
    } else {
      return;
    }

    setDetailModalSourcePage();
    router.push(url);
  };

  return navigate;
};

export function DetailModalLink({
  pathname,
  children,
  className,
  target,
  ignoreDefaultClassName,
  ref,
  ...props
}: DetailModalLinkProps) {
  const currentPathname = usePathname();
  const query = useSearchParams().toString();

  const isDetailPage = currentPathname.includes('/detail/');
  const setDetailModalSourcePage = () => {
    if (isDetailPage) return;

    writeToSessionStorage({
      key: DETAIL_MODAL_SOURCE_KEY,
      value: query ? `${currentPathname}?${query}` : currentPathname,
    });
  };

  if (!children) return '-';

  return (
    <Link
      prefetch={false}
      onClick={setDetailModalSourcePage}
      target={target}
      ref={ref}
      {...props}
      className={cn(
        ignoreDefaultClassName
          ? className
          : buttonVariants({ variant: 'link', size: 'sm' }),
        'h-auto px-0 whitespace-nowrap',
        className
      )}
      href={pathname}
    >
      {children}
    </Link>
  );
}
